import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import { 
  setupVisualizerColors, 
  getVisualizerConfig, 
  applyVisualizerScaling 
} from './midiVisualizerUtils';
import ConsentForm from './ConsentForm';
import MultitrackExamples from './MultitrackExamples';
import LeadSheetExamples from './LeadSheetExamples';

// Backend API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL;

// Raise an error if the API_BASE_URL is not set
if (!API_BASE_URL) {
  throw new Error('REACT_APP_API_URL is not set');
}



// Custom MIDI Player Component with Feedback
const MidiPlayer = ({ midiData, index, originalIndex, generationMetadata }) => {
  const containerRef = useRef(null);
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const visualizerDivRef = useRef(null);
  const playerRef = useRef(null);
  
  // Feedback system state
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(null); // 'super_great', 'good', 'bad', or null
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  
  // Qualitative feedback state
  const [showQualitativeFeedback, setShowQualitativeFeedback] = useState(false);
  const [qualitativeFeedbackText, setQualitativeFeedbackText] = useState('');

  // Get MIDI hash from backend-provided hashes (backend is the single source of truth)
  const getMidiHash = () => {
    if (generationMetadata?.all_midi_hashes && generationMetadata.all_midi_hashes[index]) {
      const hash = generationMetadata.all_midi_hashes[index];
      // Don't return "FAIL" hashes - that indicates the generation failed
      if (hash !== "FAIL") {
        return hash;
      }
    }
    // No fallback needed - backend always provides hashes
    console.error('MIDI hash not provided by backend');
    return null;
  };

  // Submit feedback to backend
  const submitFeedback = async (rating) => {
    const genNum = (originalIndex !== undefined ? originalIndex : index) + 1;
    console.log(`[Gen ${genNum}] Starting feedback submission: ${rating}`);
    setIsSubmittingFeedback(true);
    try {
      const midiHash = getMidiHash();
      if (!midiHash) {
        throw new Error('Failed to generate MIDI hash');
      }
      console.log(`[Gen ${genNum}] MIDI hash: ${midiHash}`);

      const requestBody = {
        midi_hash: midiHash,
        rating: rating,
        comments: qualitativeFeedbackText.trim() || undefined, // Include qualitative feedback if exists
        generation_date: generationMetadata?.generation_date || new Date().toISOString().split('T')[0],
        generation_timestamp: generationMetadata?.generation_timestamp,
        all_midi_hashes: generationMetadata?.all_midi_hashes
      };
      console.log(`[Gen ${genNum}] Sending request:`, requestBody);

      const response = await fetch(`${API_BASE_URL}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify(requestBody),
      });

      console.log(`[Gen ${genNum}] Response status: ${response.status}`);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[Gen ${genNum}] Response error:`, errorText);
        throw new Error(`Failed to submit feedback: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log(`[Gen ${genNum}] Success response:`, result);

      setFeedbackSubmitted(rating);
      console.log(`[Gen ${genNum}] Feedback submitted successfully: ${rating} for MIDI ${midiHash}`);
    } catch (error) {
      console.error(`[Gen ${genNum}] Error submitting feedback:`, error);
      alert('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  // Submit qualitative feedback only
  const submitQualitativeFeedback = async () => {
    if (!qualitativeFeedbackText.trim()) return;
    
    const genNum = (originalIndex !== undefined ? originalIndex : index) + 1;
    console.log(`[Gen ${genNum}] Submitting qualitative feedback only`);
    setIsSubmittingFeedback(true);
    
    try {
      const midiHash = getMidiHash();
      if (!midiHash) {
        throw new Error('Failed to get MIDI hash');
      }

      const requestBody = {
        midi_hash: midiHash,
        rating: feedbackSubmitted || 'good', // Use existing rating or default to 'good'
        comments: qualitativeFeedbackText.trim(),
        generation_date: generationMetadata?.generation_date || new Date().toISOString().split('T')[0],
        generation_timestamp: generationMetadata?.generation_timestamp,
        all_midi_hashes: generationMetadata?.all_midi_hashes
      };

      const response = await fetch(`${API_BASE_URL}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`Failed to submit feedback: ${response.status}`);
      }

      console.log(`[Gen ${genNum}] Qualitative feedback submitted successfully`);
      setShowQualitativeFeedback(false); // Close the textbox after successful submission
      
    } catch (error) {
      console.error(`[Gen ${genNum}] Error submitting qualitative feedback:`, error);
      alert('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  // Handle MIDI download
  const handleDownload = () => {
    if (!midiData) return;
    
    try {
      // Convert base64 to binary
      const binaryString = atob(midiData);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      // Generate hash for filename
      const hashStr = getMidiHash();
      
      // Create blob and download
      const blob = new Blob([bytes], { type: 'audio/midi' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `gen-${(originalIndex !== undefined ? originalIndex : index) + 1}-${hashStr}.mid`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading MIDI:', err);
    }
  };

  useEffect(() => {
    let timeoutId;
    let resizeObserver;
    const container = containerRef.current;
    if (midiData && container) {
      container.innerHTML = '';
      try {
        // Validate that midiData is a valid base64 string
        if (typeof midiData !== 'string') {
          throw new Error('MIDI data is not a string');
        }
        if (!/^[A-Za-z0-9+/]*={0,2}$/.test(midiData)) {
          throw new Error('MIDI data is not valid base64');
        }

        // Validate that it's actually a MIDI file by checking the header
        const binaryString = atob(midiData);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        if (bytes.length < 4 || String.fromCharCode(bytes[0], bytes[1], bytes[2], bytes[3]) !== 'MThd') {
          throw new Error('Invalid MIDI file format');
        }

        // Use a data URL for the MIDI file
        const dataUrl = `data:audio/midi;base64,${midiData}`;
        const visualizerId = `visualizer-${index}`;
        const visualizer = document.createElement('midi-visualizer');
        visualizer.setAttribute('type', 'piano-roll');
        visualizer.setAttribute('id', visualizerId);
        visualizer.style.width = '100%';
        visualizer.style.height = '360px';  // Give it initial space to render

        const player = document.createElement('midi-player');
        
        // Safari-specific configuration
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        if (isSafari) {
          console.log('Configuring player for Safari');
          // Use explicit SoundFont URL for Safari instead of empty string
          player.setAttribute('sound-font', 'https://storage.googleapis.com/magentadata/js/soundfonts/sgm_plus');
        } else {
          player.setAttribute('sound-font', '');
        }
        
        player.style.width = '100%';
        player.style.height = '60px';
        playerRef.current = player;
        
        // Add error event listener
        player.addEventListener('error', (e) => {
          console.error('MIDI Player error:', e);
          setError(`MIDI Player error: ${e.message || 'Unknown error'}`);
        });

        const controlsDiv = document.createElement('div');
        controlsDiv.className = 'midi-controls';
        controlsDiv.appendChild(player);

        const visualizerDiv = document.createElement('div');
        visualizerDiv.className = 'midi-visualizer';
        visualizerDiv.appendChild(visualizer);
        visualizerDivRef.current = visualizerDiv;

        container.appendChild(controlsDiv);
        container.appendChild(visualizerDiv);

        // Wait for both custom elements to be defined
        Promise.all([
          customElements.whenDefined('midi-player'),
          customElements.whenDefined('midi-visualizer')
        ]).then(() => {
          player.setAttribute('visualizer', `#${visualizerId}`);
          
          // Configure visualizer dimensions
          visualizer.config = getVisualizerConfig();
          
          // Load MIDI into visualizer immediately so piano roll shows up right away
          visualizer.src = dataUrl;
          
          // Apply colors asynchronously without blocking audio setup
          setupVisualizerColors(visualizerId, dataUrl);
          
          // Use a slightly longer delay to ensure DOM is ready
          timeoutId = setTimeout(() => {
            try {
              console.log('Setting player source, Safari:', isSafari);
              
              // For Safari, we need to ensure audio context is ready
              if (isSafari && window.Tone && window.Tone.context) {
                if (window.Tone.context.state === 'suspended') {
                  console.log('Safari: Audio context suspended, starting...');
                  // Add timeout to prevent hanging
                  Promise.race([
                    window.Tone.context.resume(),
                    new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 3000))
                  ]).then(() => {
                    console.log('Safari: Audio context resumed');
                    player.src = dataUrl;
                  }).catch(err => {
                    console.error('Safari: Failed to resume audio context:', err);
                    player.src = dataUrl; // Try anyway
                  });
                } else {
                  player.src = dataUrl;
                }
              } else {
                player.src = dataUrl;
              }
              
              setError(null);
              setIsLoaded(true);
              console.log('Player src set successfully');
            } catch (err) {
              console.error('Error setting player src:', err);
              setError(`Audio setup failed: ${err.message}`);
            }

            // Wait for the visualizer to fully render, then measure and scale
            setTimeout(() => {
              applyVisualizerScaling(visualizer, 320);
            }, 1000); // Give even more time for full rendering

            // Dynamically adjust height of the visualizer container
            if (visualizerDiv && visualizer) {
              resizeObserver = new window.ResizeObserver(entries => {
                for (let entry of entries) {
                  if (entry.target === visualizer) {
                    // Don't auto-resize if we've applied scaling
                    if (!visualizer.style.transform) {
                      visualizerDiv.style.height = entry.contentRect.height + 'px';
                    }
                  }
                }
              });
              resizeObserver.observe(visualizer);
            }
          }, 200);
        });
      } catch (err) {
        console.error('Error processing MIDI data:', err);
        setError(err.message);
        setIsLoaded(false);
      }
    }
    
    // Cleanup function
    return () => {
      clearTimeout(timeoutId);
      if (resizeObserver) resizeObserver.disconnect();
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [midiData, index]);

  if (error) {
    return (
      <div className="midi-player-container">
        <div className="midi-header">
          <h4>Generation #{(originalIndex !== undefined ? originalIndex : index) + 1}</h4>
          <button 
            className="download-btn"
            onClick={handleDownload}
            title="Download MIDI file"
            disabled={true}
          >
            ⬇ Download MIDI
          </button>
        </div>
        <div className="error-message">
          <p>❌ Error loading MIDI: {error}</p>
          <details>
            <summary>Debug Info</summary>
            <p>Data type: {typeof midiData}</p>
            <p>Data length: {midiData ? midiData.length : 0}</p>
            <p>Data preview: {midiData ? midiData.substring(0, 50) + '...' : 'null'}</p>
          </details>
        </div>
      </div>
    );
  }

  return (
    <div className="midi-player-container">
      <div className="midi-header">
        <div className="header-left">
          <h4>Generation #{(originalIndex !== undefined ? originalIndex : index) + 1}</h4>
        </div>
        <div className="header-right">
          {/* Feedback Buttons - Added after MIDI loads to avoid AudioContext issues */}
          {isLoaded && (
            <div className="feedback-section-inline">
              <span className="feedback-label">Your Rating:</span>
              <div className="feedback-buttons-inline">
                <button
                  className={`feedback-btn-round super-great ${feedbackSubmitted === 'super_great' ? 'selected' : ''}`}
                  onClick={() => submitFeedback('super_great')}
                  disabled={isSubmittingFeedback}
                  title="Super great!"
                >
                  🤩
                  <span className="feedback-text">Super great!</span>
                </button>
                <button
                  className={`feedback-btn-round good ${feedbackSubmitted === 'good' ? 'selected' : ''}`}
                  onClick={() => submitFeedback('good')}
                  disabled={isSubmittingFeedback}
                  title="Good"
                >
                  😊
                  <span className="feedback-text">Good</span>
                </button>
                <button
                  className={`feedback-btn-round bad ${feedbackSubmitted === 'bad' ? 'selected' : ''}`}
                  onClick={() => submitFeedback('bad')}
                  disabled={isSubmittingFeedback}
                  title="Bad"
                >
                  😞
                  <span className="feedback-text">Bad</span>
                </button>
                
                {/* Qualitative Feedback Button - grouped with rating buttons */}
                <button
                  className="qualitative-feedback-btn"
                  onClick={() => setShowQualitativeFeedback(!showQualitativeFeedback)}
                  disabled={isSubmittingFeedback}
                  title="Add written feedback"
                >
                  More to say?
                </button>
              </div>
            </div>
          )}
          <button 
            className="download-btn"
            onClick={handleDownload}
            title="Download MIDI file"
          >
            ⬇ Download MIDI
          </button>
        </div>
      </div>
      
      {/* Qualitative Feedback Section */}
      {isLoaded && showQualitativeFeedback && (
        <div className="qualitative-feedback-section">
          <textarea
            className="qualitative-feedback-textarea"
            value={qualitativeFeedbackText}
            onChange={(e) => setQualitativeFeedbackText(e.target.value)}
            placeholder="Tell us more about your thoughts on this generation..."
            rows={3}
            disabled={isSubmittingFeedback}
          />
          <div className="qualitative-feedback-actions">
            <button
              className="qualitative-submit-btn"
              onClick={submitQualitativeFeedback}
              disabled={!qualitativeFeedbackText.trim() || isSubmittingFeedback}
            >
              Submit
            </button>
            <button
              className="qualitative-cancel-btn"
              onClick={() => setShowQualitativeFeedback(false)}
              disabled={isSubmittingFeedback}
            >
              Close
            </button>
          </div>
        </div>
      )}
      
      {/* Feedback Status Info */}
      {isLoaded && (
        <div className="feedback-status">
          <p className="feedback-message">
            {isSubmittingFeedback ? (
              <span className="feedback-loading">Submitting feedback...</span>
            ) : feedbackSubmitted ? (
              <span className="feedback-thanks">Thanks for rating! You can always change it. ✨</span>
            ) : null}
          </p>
        </div>
      )}
      
      {!isLoaded && (
        <div className="loading-message">
          <p>🔄 Loading MIDI player...</p>
        </div>
      )}
      
      <div ref={containerRef}></div>
    </div>
  );
};



function App() {
  const [prompt, setPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('');
  const [examplePrompts, setExamplePrompts] = useState([]);
  const [selectedExample, setSelectedExample] = useState(null);
  
  // Backend status states
  const [backendStatus, setBackendStatus] = useState('checking'); // 'checking', 'ready', 'not_ready', 'error'
  const [backendMessage, setBackendMessage] = useState('Checking backend status...');
  // While the GPU backend is waking up (status 'not_ready'), re-check every few seconds (bounded)
  const statusPoll = useRef({ timer: null, tries: 0 });
  const STATUS_POLL_MS = 5000;
  const STATUS_POLL_MAX_TRIES = 84; // ~7 minutes (a cold GPU boot takes ~3-4)
  const STATUS_TIMEOUT_MS = 15000;

  // GPU wake-up + real generation progress (Modal backend)
  const [wakingSince, setWakingSince] = useState(null); // ms epoch when the GPU began booting; null = not booting
  const [genPhase, setGenPhase] = useState(null); // null | 'waking' | 'generating'
  const [tokenProgress, setTokenProgress] = useState(null); // { tokens, total } from the backend
  const [, setClockTick] = useState(0);
  useEffect(() => {
    if (wakingSince === null) return undefined;
    const id = setInterval(() => setClockTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [wakingSince]);
  const wakingElapsed = wakingSince === null ? 0 : Math.max(0, Math.floor((Date.now() - wakingSince) / 1000));
  const fmtClock = (sec) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
  // Second line under the waiting message: sent by the backend, so the copy matches its actual start-up
  const [wakingHint, setWakingHint] = useState(null);
  const [generateHint, setGenerateHint] = useState(null);
  const markWaking = (elapsedSec) => setWakingSince(prev => prev ?? Date.now() - (elapsedSec || 0) * 1000);

  // Consent form states
  const [hasConsented, setHasConsented] = useState(false);
  const [hasBypassed, setHasBypassed] = useState(false);
  const [isCheckingConsent, setIsCheckingConsent] = useState(true);

  // Demo mode state
  const [demoMode, setDemoMode] = useState('live'); // 'live' | 'multitrack' | 'leadsheet'

  // Example prompts with icons and labels
  const baseExamplePrompts = [
    {
      label: 'Epic Rock',
      icon: '🎸',
      text: 'Rock piece suitable for a hero\'s epic battle against a dark villain. The music is rich with synth leads, brass section, and heavy drums.'
    },
    {
      label: 'Slow Classical',
      icon: '🎻',
      text: 'Slow and loving music that is best for a romantic film. The piano and string ensemble give a classical vibe.'
    },
    {
      label: 'Playful Jazz',
      icon: '🎷',
      text: 'Upbeat and playful jazz music with lively saxophones, like you\'re going out on a Sunday picnic.'
    },
    {
      label: 'Energetic Pop',
      icon: '🕺',
      text: 'An energetic and motivating pop song you love to hear on a long road trip.'
    },
    {
      label: 'Sad & Emotional',
      icon: '💔',
      text: 'A sad and emotional song featuring relentless, heartbreaking acoustic guitars.'
    }
  ];



  // Check backend status
  const scheduleStatusPoll = () => {
    clearTimeout(statusPoll.current.timer);
    if (statusPoll.current.tries >= STATUS_POLL_MAX_TRIES) return;
    statusPoll.current.tries += 1;
    statusPoll.current.timer = setTimeout(() => checkBackendStatus(), STATUS_POLL_MS);
  };

  const checkBackendStatus = async () => {
    clearTimeout(statusPoll.current.timer);
    try {
      // Add timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), STATUS_TIMEOUT_MS);
      
      const response = await fetch(`${API_BASE_URL}/status`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.model_loaded) {
        setBackendStatus('ready');
        setBackendMessage(data.message);
        setWakingSince(null);
        statusPoll.current.tries = 0;
      } else {
        setBackendStatus('not_ready');
        setBackendMessage(data.message);
        markWaking(data.waking_elapsed_s);
        setWakingHint(data.hint || null);
        setGenerateHint(data.hint_generate || null);
        scheduleStatusPoll(); // backend is waking up: check again shortly
      }
    } catch (error) {
      console.error('Error checking backend status:', error);
      setBackendStatus('error');
      
      // More specific error messages
      if (error.name === 'AbortError') {
        setBackendMessage('LLM server is not running/ready now, please come back later in a few moments :) Or, you might be behind a protected network.');
      } else if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        setBackendMessage('LLM server is not running/ready now, please come back later in a few moments :) Or, you might be behind a protected network.');
      } else {
        setBackendMessage('LLM server is not running/ready now, please come back later in a few moments :) Or, you might be behind a protected network.');
      }
    }
  };

  // Check for existing consent on mount
  useEffect(() => {
    const checkConsent = () => {
      try {
        const consentData = localStorage.getItem('midi-llm-consent');
        if (consentData) {
          const consent = JSON.parse(consentData);
          if (consent.termsAccepted && consent.ageConfirmed) {
            setHasConsented(true);
          }
        }
        
        const bypassData = localStorage.getItem('midi-llm-bypass');
        if (bypassData) {
          const bypass = JSON.parse(bypassData);
          if (bypass.bypassed) {
            setHasBypassed(true);
          }
        }
      } catch (error) {
        console.error('Error checking consent:', error);
        // If there's an error, clear potentially corrupted data
        localStorage.removeItem('midi-llm-consent');
        localStorage.removeItem('midi-llm-bypass');
      }
      setIsCheckingConsent(false);
    };

    checkConsent();
  }, []);

  // Randomize example prompts order on component mount
  useEffect(() => {
    const shuffled = [...baseExamplePrompts].sort(() => Math.random() - 0.5);
    setExamplePrompts(shuffled);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Check backend status on mount; re-checks only while the backend reports it is waking up
  useEffect(() => {
    checkBackendStatus();
    const poll = statusPoll.current;
    return () => clearTimeout(poll.timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Set static mode by default for bypassed users
  useEffect(() => {
    if (hasBypassed && !hasConsented && demoMode === 'live') {
      setDemoMode('multitrack');
    }
  }, [hasBypassed, hasConsented, demoMode]);

  // Handle consent acceptance
  const handleConsentAccepted = () => {
    setHasConsented(true);
    setDemoMode('live'); // Direct user to Live Demo after consenting
  };

  // Handle bypass
  const handleBypass = () => {
    localStorage.setItem('midi-llm-bypass', JSON.stringify({
      timestamp: new Date().toISOString(),
      bypassed: true
    }));
    setHasBypassed(true);
  };

  // Handle return to consent form
  const handleReturnToConsent = () => {
    localStorage.removeItem('midi-llm-bypass');
    setHasBypassed(false);
  };

  // Handle example prompt click
  const handleExampleClick = (exampleText, index) => {
    setPrompt(exampleText);
    setSelectedExample(index);
  };

  // Check if generation should be disabled
  const isGenerationDisabled = backendStatus !== 'ready' || isLoading || !prompt.trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!prompt.trim()) return;

    setIsLoading(true);
    setProgress(0);
    setStatus('Checking backend status...');
    setResponse(null);
    setGenPhase(null);
    setTokenProgress(null);

    const progressState = { id: null };

    try {
      // First, check if backend is still ready
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), STATUS_TIMEOUT_MS);
      
      const statusResponse = await fetch(`${API_BASE_URL}/status`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!statusResponse.ok) {
        throw new Error(`Backend unavailable! status: ${statusResponse.status}`);
      }

      const statusData = await statusResponse.json();
      
      if (!statusData.model_loaded) {
        // The GPU fell asleep since the page loaded: go ahead anyway, the request waits for it to boot
        setGenPhase('waking');
        markWaking(statusData.waking_elapsed_s);
        setGenerateHint(statusData.hint_generate || null);
        setStatus('Starting the GPU server');
      } else {
        setStatus('Spinning LLM up ...');
      }
      
      const generateWithSSE = async () => {
        while (true) {
          try {
            // Try to start the SSE stream
            const response = await fetch(`${API_BASE_URL}/generate-stream`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'ngrok-skip-browser-warning': 'true',
              },
              body: JSON.stringify({
                prompt: prompt,
                temperature: 1.0,
                top_p: 0.98,
                n_requested_generations: 4
              }),
            });

            if (response.status === 503) {
              // Server is busy, wait and retry
              setStatus('⏳ Another generation is in progress. Waiting in queue...');
              await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds
              continue; // Retry
            }

            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }

            // Set up SSE reader
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            
            return new Promise((resolve, reject) => {
              const readStream = async () => {
                try {
                  const { value, done } = await reader.read();
                  if (done) return;
                  
                  buffer += decoder.decode(value, { stream: true });
                  const messages = buffer.split('\n\n');
                  
                  // Keep the last potentially incomplete message in buffer
                  buffer = messages.pop() || '';
                  
                  for (const message of messages) {
                    if (message.trim()) {
                      const lines = message.split('\n');
                      let eventType = '';
                      let data = '';
                      
                      for (const line of lines) {
                        if (line.startsWith('event: ')) {
                          eventType = line.substring(7);
                        } else if (line.startsWith('data: ')) {
                          data = line.substring(6);
                        }
                      }
                      
                      // Handle different event types
                      if (eventType === 'waking') {
                        // Cold GPU: the backend is booting it; show the waiting state with a live clock
                        let info = {};
                        try { info = JSON.parse(data); } catch (e) { /* keep defaults */ }
                        setGenPhase('waking');
                        markWaking(info.elapsed_s);
                        if (info.hint) setGenerateHint(info.hint);
                        setStatus('Starting the GPU server');

                      } else if (eventType === 'progress') {
                        // Real progress from the backend (tokens generated so far): replaces the simulation
                        let p = null;
                        try { p = JSON.parse(data); } catch (e) { /* ignore malformed */ }
                        if (p) {
                          if (progressState.id) {
                            clearInterval(progressState.id);
                            progressState.id = null;
                          }
                          setProgress(Math.min(90, 5 + 0.85 * p.pct));
                          setTokenProgress({ tokens: p.tokens, total: p.total });
                          setStatus(`Running LLM inference... ${Math.round(p.pct)}%`);
                        }

                      } else if (eventType === 'generation_started') {
                        // Start progress bar when generation actually begins
                        setGenPhase('generating');
                        setWakingSince(null);
                        setStatus('Spinning LLM up ...');
                        setProgress(0);
                        
                        // Start smooth progress simulation
                        progressState.id = setInterval(() => {
                          setProgress(prev => {
                            if (prev < 90) {
                              const newProgress = Math.min(90, prev + 2); // 2% increments instead of 10%
                              setStatus(`Running LLM inference... ${Math.round(newProgress)}%`);
                              return newProgress;
                            }
                            return prev;
                          });
                        }, 200); // Update every 200ms for smooth animation
                        
                      } else if (eventType === 'generation_complete') {
                        try {
                          const result = JSON.parse(data);
                          
                          // Use the exact timestamp from the server for feedback submission
                          result.generation_timestamp = result.timestamp;
                          result.generation_date = result.timestamp.split('T')[0];
                          
                          // Clear progress interval
                          if (progressState.id) {
                            clearInterval(progressState.id);
                          }
                          
                          // Complete the progress
                          setStatus('Converting to MIDI ...');
                          setProgress(95);
                          
                          // Small delay to show the conversion step
                          setTimeout(() => {
                            setStatus('Generation complete!');
                            setProgress(100);
                            setResponse(result);
                            setIsLoading(false);
                            setGenPhase(null);
                            setTokenProgress(null);
                            resolve(result);
                          }, 500);
                          
                        } catch (parseError) {
                          console.error('Error parsing generation result:', parseError);
                          reject(new Error('Failed to parse generation result'));
                        }
                        
                      } else if (eventType === 'error') {
                        try {
                          const error = JSON.parse(data);
                          reject(new Error(error.error));
                        } catch (parseError) {
                          reject(new Error('Server error occurred'));
                        }
                      }
                    }
                  }
                  
                  // Continue reading
                  readStream();
                } catch (error) {
                  reject(error);
                }
              };
              
              readStream();
            });
            
          } catch (error) {
            throw error; // Re-throw non-503 errors
          }
        }
      };

      // Start generation with SSE
      await generateWithSSE();

    } catch (error) {
      // Clear progress interval if it was started
      if (progressState.id) {
        clearInterval(progressState.id);
      }
      
      console.error('Error:', error);
      setStatus(`Error: ${error.message}`);
      setIsLoading(false);
      setGenPhase(null);
      setTokenProgress(null);
      setWakingSince(null);
      
      // If it's a backend availability error, update the backend status
      if (error.message.includes('Backend unavailable') || error.message.includes('Failed to fetch')) {
        setBackendStatus('error');
        setBackendMessage('LLM server is not running/ready now, please come back later in a few moments :) Or, you might be behind a protected network.');
        setResponse(null); // Clear previous results so status message is visible
      }
    }
  };

  // Show loading while checking consent
  if (isCheckingConsent) {
    return (
      <div className="App">
        <div className="container">
          <div className="header">
            <h1>🎸 MIDI-LLM 🪗</h1>
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show consent form if user hasn't consented and hasn't bypassed
  if (!hasConsented && !hasBypassed) {
    return (
      <div className="App">
        <ConsentForm onConsent={handleConsentAccepted} onBypass={handleBypass} />
      </div>
    );
  }



  return (
    <div className="App">
      <div className="container">
        <div className="header">
          <h1>🎸 MIDI-LLM 🪗</h1>
          <p className="header-subtitle">Improving Text-to-MIDI Music Generation via Adapting Large Language Models</p>

          {/* Authors and Affiliation */}
          <div className="authors-block">
            <div className="authors-affiliation">
              <span className="author-name">Shih-Lun Wu<sup className="music-symbol">♯</sup></span>
              <span className="author-name">Dave Carlton<sup className="music-symbol">♭</sup></span>
              <span className="author-name">Ryan Miyakawa<sup className="music-symbol">♭</sup></span>
              <span className="author-name">Yoon Kim<sup className="music-symbol">♯</sup></span>
              <span className="author-name">Chris Donahue<sup className="music-symbol">♮♪</sup></span>
              <span className="author-name">Cheng-Zhi Anna Huang<sup className="music-symbol">♯♪</sup></span>
            </div>
            <p className="affiliation-legend">
              <span className="music-symbol">♯</span> <span className="affiliation-name">MIT</span>
              <span className="affiliation-separator">&middot;</span>
              <span className="music-symbol">♭</span> <span className="affiliation-name">Hooktheory</span>
              <span className="affiliation-separator">&middot;</span>
              <span className="music-symbol">♮</span> <span className="affiliation-name">CMU</span>
              <span className="affiliation-separator">&middot;</span>
              <span className="contribution-note"><span className="music-symbol">♪</span> Equal senior contribution</span>
            </p>
          </div>

          {/* Links Banner */}
          <div className="header-links">
            <span className="header-link-disabled">ISMIR 2026</span>
            <span className="link-separator">|</span>
            <a href="https://github.com/slSeanWU/MIDI-LLM" target="_blank" rel="noopener noreferrer" className="header-link">
              💻 <span className="link-text">Code</span>
            </a>
            <span className="link-separator">|</span>
            <a href="https://huggingface.co/slseanwu/MIDI-LLM_Llama-3.2-1B" target="_blank" rel="noopener noreferrer" className="header-link">
              🤗 <span className="link-text">Model</span>
            </a>
            <span className="link-separator">|</span>
            <a href="https://arxiv.org/abs/2511.03942" target="_blank" rel="noopener noreferrer" className="header-link">
              📑 <span className="link-text">Paper</span>
            </a>
          </div>
          
          {/* Demo Mode Toggle */}
          <div className="demo-mode-toggle">
            <div className="toggle-buttons">
              <button
                className={`toggle-btn toggle-btn-live ${demoMode === 'live' ? 'active' : ''}`}
                onClick={() => {
                  if (hasConsented) {
                    setDemoMode('live');
                  } else {
                    // Show consent form by clearing bypass and setting consent to false
                    localStorage.removeItem('midi-llm-bypass');
                    setHasBypassed(false);
                    // The consent form will show automatically
                  }
                }}
                title={!hasConsented ? "Click to view consent form and access Live Demo" : ""}
              >
                <div className="toggle-btn-content">
                  <span className="toggle-btn-main">🪗 Live Demo</span>
                </div>
              </button>
              <button
                className={`toggle-btn toggle-btn-multitrack ${demoMode === 'multitrack' ? 'active' : ''}`}
                onClick={() => setDemoMode('multitrack')}
              >
                <div className="toggle-btn-content">
                  <span className="toggle-btn-main">Multitrack Examples</span>
                  <span className="toggle-btn-sub">LakhMIDI &middot; MIDI-LLM vs. Text2midi</span>
                </div>
              </button>
              <button
                className={`toggle-btn toggle-btn-leadsheet ${demoMode === 'leadsheet' ? 'active' : ''}`}
                onClick={() => setDemoMode('leadsheet')}
              >
                <div className="toggle-btn-content">
                  <span className="toggle-btn-main">Lead Sheet Examples</span>
                  <span className="toggle-btn-sub">TheoryTab &middot; attribute control</span>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Live Demo Content */}
        {demoMode === 'live' && (
          <>
            {/* Backend Status Display */}
            {backendStatus === 'error' && (
              <div className="backend-status-message error">
                <p>⚠️ {backendMessage}</p>
                <div className="status-buttons">
                  <button onClick={checkBackendStatus} className="check-status-btn">
                    Try Again
                  </button>
                  <button 
                    onClick={() => setDemoMode('multitrack')} 
                    className="go-to-static-btn"
                  >
                    Go to Static Examples
                  </button>
                </div>
              </div>
            )}
            {backendStatus === 'not_ready' && (
              <div className="backend-status-message not-ready">
                <p>⏳ {backendMessage}</p>
                {wakingSince !== null && wakingHint && (
                  <p className="waking-hint">{wakingHint}</p>
                )}
                {wakingSince !== null && (
                  <div className="waking-indicator" role="status" aria-label={`Starting the GPU server, ${fmtClock(wakingElapsed)} elapsed`}>
                    <div className="waking-track"><div className="waking-glide"></div></div>
                    <span className="waking-clock">{fmtClock(wakingElapsed)}</span>
                  </div>
                )}
                <div className="status-buttons">
                  <button onClick={checkBackendStatus} className="check-status-btn">
                    Check Status
                  </button>
                  <button 
                    onClick={() => setDemoMode('multitrack')} 
                    className="go-to-static-btn"
                  >
                    Go to Static Examples
                  </button>
                </div>
              </div>
            )}
            {backendStatus === 'checking' && (
              <div className="backend-status-message checking">
                <p>🔄 {backendMessage}</p>
              </div>
            )}

            {/* Only show form when backend is ready */}
            {backendStatus === 'ready' && (
          <form onSubmit={handleSubmit} className="prompt-form">
            <div className="form-group">
              <label htmlFor="prompt">Describe the music you want to generate:</label>
              
              {/* Example Prompts */}
              <div className="example-prompts">
                <p className="example-prompts-label">Or try one of these examples:</p>
                <div className="example-buttons">
                  {examplePrompts.map((example, index) => (
                    <button
                      key={index}
                      type="button"
                      className={`example-btn ${selectedExample === index ? 'selected' : ''}`}
                      onClick={() => handleExampleClick(example.text, index)}
                      disabled={isLoading}
                    >
                      <span className="example-icon">{example.icon}</span>
                      <span className="example-label">{example.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Helper hint */}
              <p className="helper-hint">(hint: you can try out genre, vibes, tempo, instruments, keys, chords, or even a scene/storyline you imagine in the prompt)</p>

              <textarea
                id="prompt"
                value={prompt}
                onChange={(e) => {
                  setPrompt(e.target.value);
                  // Clear selection if user manually types
                  if (selectedExample !== null) {
                    const selectedText = examplePrompts[selectedExample]?.text;
                    if (e.target.value !== selectedText) {
                      setSelectedExample(null);
                    }
                  }
                }}
                placeholder={examplePrompts.length > 0 ? `e.g., ${examplePrompts[0].text}` : "e.g., A peaceful pop song perfect for a relaxing Sunday afternoon ..."}
                rows={4}
                className="prompt-input"
                disabled={isLoading}
              />
            </div>
            <button 
              type="submit" 
              disabled={isGenerationDisabled}
              className="generate-btn"
            >
              {isLoading ? 'Generating...' : 'Generate Music'}
            </button>
          </form>
        )}

        {/* Progress Bar */}
        {isLoading && (
          <div className="progress-section">
            <div className={`progress-bar${genPhase === 'waking' ? ' waking' : ''}`}>
              <div 
                className="progress-fill" 
                style={genPhase === 'waking' ? undefined : { width: `${progress}%` }}
              ></div>
            </div>
            <p className="progress-status" role="status">
              {status}
              {genPhase === 'waking' && <span className="progress-clock">{fmtClock(wakingElapsed)}</span>}
            </p>
            {genPhase === 'waking' && generateHint && (
              <p className="progress-hint">{generateHint}</p>
            )}
            {genPhase === 'generating' && tokenProgress && (
              <p className="progress-hint">{tokenProgress.tokens.toLocaleString()} / {tokenProgress.total.toLocaleString()} MIDI tokens</p>
            )}
          </div>
        )}

        {/* don't change to a new line for the generation time, but should use a subdued text and typeface, put some space after `Your Generations` */}

        {response && (
          <div className="response-section">
            <h3>Your Generations <span style={{ color: '#666', fontFamily: 'Inter', fontSize: '14px', fontWeight: '400' , paddingLeft: '10px'}}>({response.n_successful_generations} / {response.n_requested_generations} successful in {response.generation_time.toFixed(2)}s on {response.gpu || 'A6000'} GPU)</span></h3>
            {(() => {
              // Create arrays that only include successful generations and their corresponding hashes
              const successfulMidis = [];
              const successfulHashes = [];
              const successfulOriginalIndices = [];
              
              response.generated_midis.forEach((midi, originalIndex) => {
                if (midi !== null) {
                  successfulMidis.push(midi);
                  successfulHashes.push(response.all_midi_hashes ? response.all_midi_hashes[originalIndex] : null);
                  successfulOriginalIndices.push(originalIndex);
                }
              });
              
              return successfulMidis.map((midi, filteredIndex) => (
                <div key={successfulOriginalIndices[filteredIndex]} className="midi-item">
                  <MidiPlayer 
                    midiData={midi} 
                    index={filteredIndex}
                    originalIndex={successfulOriginalIndices[filteredIndex]}
                    generationMetadata={{
                      ...response,
                      all_midi_hashes: successfulHashes  // Only hashes for successful generations
                    }} 
                  />
                </div>
              ));
            })()}
          </div>
        )}
          </>
        )}

        {/* Multitrack (Lakh / MidiCaps) Examples */}
        {demoMode === 'multitrack' && (
          <MultitrackExamples
            onReturnToLive={hasConsented ? () => setDemoMode('live') : handleReturnToConsent}
          />
        )}

        {/* Lead Sheet (TheoryTab) Examples */}
        {demoMode === 'leadsheet' && (
          <LeadSheetExamples
            onReturnToLive={hasConsented ? () => setDemoMode('live') : handleReturnToConsent}
          />
        )}
      </div>
    </div>
  );
}

export default App; 
